import { createRouter, createWebHistory } from "vue-router"
import type { Component } from "vue"
import HomeDashboard from "./views/HomeDashboard.vue"
import MerchantGroupDashboard from "./views/merchantGroup/MerchantGroupDashboard.vue"
import HomeIcon from "~icons/heroicons/home"
import PortalLogIn from "./views/PortalLogIn.vue"
import MerchantGroupLogin from "./views/merchantGroup/MerchantGroupLogin.vue"
import PortalLayout from "@/layouts/PortalLayout.vue"
import MerchantGroupLayout from "@/layouts/MerchantGroupLayout.vue"
import PortalSettings from "./views/PortalSettings.vue"
import PortalHandleLoginLink from "./views/PortalHandleLoginLink.vue"
import ReportsTransactions from "./views/ReportsTransactions.vue"
import ReportsTopProducts from "./views/ReportsTopProducts.vue"
import ReportsLayout from "@/layouts/ReportsLayout.vue"
import ReportsSales from "./views/ReportsSales.vue"
import ProductsIndex from "./views/ProductsIndex.vue"
import CategoriesIndex from "./views/CategoriesIndex.vue"
import ProductEdit from "./views/ProductEdit.vue"
import ProductCreate from "./views/ProductCreate.vue"
import UsersIndex from "@/views/UsersIndex.vue"
import UserCreate from "@/views/UserCreate.vue"
import UserEdit from "@/views/UserEdit.vue"
import CategoryEdit from "./views/CategoryEdit.vue"
import CategoryCreate from "./views/CategoryCreate.vue"

import CashiersIndex from "./views/CashiersIndex.vue"
import CashierEdit from "./views/CashierEdit.vue"
import CashierCreate from "./views/CashierCreate.vue"
import CashierManageIcon from "~icons/ant-design/user-outlined"

import OrdersIndex from "./views/OrdersIndex.vue"
import OrderDetail from "./views/OrderDetail.vue"
import OrdersIndexIcon from "~icons/mdi/cart-sale"
import DonationDetail from "@/views/DonationDetail.vue"
import TransactionsIndex from "@/views/TransactionsIndex.vue"
import TransactionsIndexIcon from "~icons/fa-solid/receipt"

import TransactionReportIcon from "~icons/uil/transaction"
import SalesReportIcon from "~icons/mdi/cart-sale"
import ProductManageIcon from "~icons/fluent-mdl2/product-release"
import CategoryManageIcon from "~icons/carbon/category"
import SettingsIcon from "~icons/ci/more-vertical"
import TopProductsReportIcon from "~icons/mdi/graph-bar"

import ImportIndex from "./views/ProductImportIndex.vue"
import ImportDetail from "./views/ProductImportDetail.vue"
import ImportIndexIcon from "~icons/heroicons/arrow-up-tray"
import ChangePassword from "./views/ChangePassword.vue"
import MerchantGroupChangePassword from "./views/merchantGroup/MerchantGroupChangePassword.vue"
import MerchantGroupUserEdit from "@/views/merchantGroup/MerchantGroupUserEdit.vue"
import MerchantGroupUserCreate from "@/views/merchantGroup/MerchantGroupUserCreate.vue"

import AdvancePlus from "./views/AdvancePlus.vue"
import MerchantGroupUsersIndex from "./views/merchantGroup/MerchantGroupUsersIndex.vue"
import UsersIcon from "~icons/ion/people-circle-outline"
import PortalHandleMerchantGroupLoginLink from "./views/merchantGroup/PortalHandleMerchantGroupLoginLink.vue"

const { setPageTitle } = usePageTitle()
const { isLoggedIn } = useAuth()
const { isLoggedInMerchantGroup } = useMerchantGroupAuth()

declare module "vue-router" {
  interface RouteMeta {
    auth?: boolean
    merchantGroupAuth?: boolean
    pageTitle: string
    icon?: Component
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/merchant-group",
      name: "",
      component: MerchantGroupLayout,
      meta: {
        merchantGroupAuth: true,
        pageTitle: "",
      },
      children: [
        {
          path: "",
          name: "homeMerchantGroup",
          component: MerchantGroupDashboard,
          meta: {
            pageTitle: "Merchants",
            icon: HomeIcon,
          },
        },

        {
          path: "users",
          name: "merchantGroupUsersIndex",
          component: MerchantGroupUsersIndex,
          meta: {
            pageTitle: "Users",
            icon: UsersIcon,
          },
        },
        {
          path: "users/:userId/edit",
          name: "merchantGroupUserEdit",
          component: MerchantGroupUserEdit,
          meta: {
            pageTitle: "Edit User",
          },
          props: true,
        },
        {
          path: "users/new",
          name: "merchantGroupUserCreate",
          component: MerchantGroupUserCreate,
          meta: {
            pageTitle: "Create User",
          },
        },
        {
          path: "change-password",
          name: "changePasswordMerchantGroup",
          component: MerchantGroupChangePassword,
          meta: {
            pageTitle: "Change Password",
          },
        },
      ],
    },
    {
      path: "/",
      component: PortalLayout,
      meta: {
        auth: true,
        pageTitle: "",
      },
      children: [
        {
          path: "",
          name: "home",
          component: HomeDashboard,
          meta: {
            pageTitle: "Dashboard",
            icon: HomeIcon,
          },
        },
        {
          path: "advance-plus",
          name: "advancePlus",
          component: AdvancePlus,
          meta: {
            pageTitle: "Advance+",
          },
        },
        {
          path: "change-password",
          name: "changePassword",
          component: ChangePassword,
          meta: {
            pageTitle: "Change Password",
          },
        },
        {
          path: "settings",
          name: "settings",
          component: PortalSettings,
          meta: {
            pageTitle: "Settings",
            icon: SettingsIcon,
          },
        },
        {
          path: "users",
          name: "users",
          meta: {
            pageTitle: "Users",
            icon: UsersIcon,
          },
          children: [
            {
              path: "",
              name: "usersIndex",
              component: UsersIndex,
              meta: {
                pageTitle: "Users",
              },
            },
            {
              path: "new",
              name: "userCreate",
              component: UserCreate,
              meta: {
                pageTitle: "Add New User",
              },
            },
            {
              path: ":userId",
              name: "userEdit",
              component: UserEdit,
              meta: {
                pageTitle: "Edit User",
              },
              props: true,
            },
          ],
        },
        {
          path: "reports",
          component: ReportsLayout,
          name: "reportsIndex",
          redirect: {
            name: "reportsTransactions",
          },
          meta: {
            pageTitle: "Reports",
          },
          children: [
            {
              path: "transactions",
              name: "reportsTransactions",
              component: ReportsTransactions,
              meta: {
                pageTitle: "Transactions Report",
                icon: TransactionReportIcon,
              },
            },
            {
              path: "sales",
              name: "reportsSales",
              component: ReportsSales,
              meta: {
                pageTitle: "Sales Report",
                icon: SalesReportIcon,
              },
            },
            {
              path: "top-products",
              name: "reportsTopProducts",
              component: ReportsTopProducts,
              meta: {
                pageTitle: "Top Products Report",
                icon: TopProductsReportIcon,
              },
            },
            {
              path: ":pathMatch(.*)*",
              redirect: {
                name: "reportsTransactions",
              },
            },
          ],
        },
        {
          path: "history",
          name: "history",
          redirect: { name: "historyOrders" },
          // component: HistoryLayout,
          meta: {
            pageTitle: "History",
          },
          children: [
            {
              path: "orders",
              name: "historyOrders",
              component: OrdersIndex,
              meta: {
                pageTitle: "Sales History",
                icon: OrdersIndexIcon,
              },
            },
            {
              path: "orders/:orderId",
              name: "historyOrdersDetail",
              component: OrderDetail,
              props: true,
              meta: {
                pageTitle: "Sale Detail",
              },
            },

            {
              path: "donations/:donationId",
              name: "historyDonationDetail",
              component: DonationDetail,
              props: true,
              meta: {
                pageTitle: "Donation Detail",
              },
            },
            {
              path: "transactions",
              name: "historyTransactions",
              component: TransactionsIndex,
              meta: {
                pageTitle: "Transaction History",
                icon: TransactionsIndexIcon,
              },
            },
          ],
        },
        {
          path: "manage",
          name: "manageMenu",
          redirect: { name: "manageProducts" },
          meta: {
            pageTitle: "Manage",
          },
          children: [
            {
              path: "products",

              children: [
                {
                  path: "",
                  name: "manageProducts",
                  component: ProductsIndex,
                  meta: {
                    pageTitle: "Manage Products",
                    icon: ProductManageIcon,
                  },
                },
                {
                  path: ":productId",
                  name: "manageProductsEdit",
                  component: ProductEdit,
                  props: true,
                  meta: {
                    pageTitle: "Edit Product",
                  },
                },
                {
                  path: "new",
                  name: "manageProductsCreate",
                  component: ProductCreate,
                  meta: {
                    pageTitle: "Add Product",
                  },
                },
              ],
            },
            {
              path: "categories",
              children: [
                {
                  path: "",
                  name: "manageCategories",
                  component: CategoriesIndex,
                  meta: {
                    pageTitle: "Manage Categories",
                    icon: CategoryManageIcon,
                  },
                },
                {
                  path: "categories/:categoryId",
                  name: "manageCategoriesEdit",
                  component: CategoryEdit,
                  props: true,
                  meta: {
                    pageTitle: "Edit Category",
                  },
                },
                {
                  path: "categories/new",
                  name: "manageCategoriesCreate",
                  component: CategoryCreate,
                  meta: {
                    pageTitle: "Add Category",
                  },
                },
              ],
            },
            {
              path: "imports",
              children: [
                {
                  path: "",
                  name: "importIndex",
                  component: ImportIndex,
                  meta: {
                    pageTitle: "Import/Export",
                    icon: ImportIndexIcon,
                  },
                },
                {
                  path: ":importId",
                  name: "importDetail",
                  component: ImportDetail,
                  props: true,
                  meta: {
                    pageTitle: "Review Import",
                  },
                },
              ],
            },
            {
              path: "cashiers",
              children: [
                {
                  path: "",
                  name: "manageCashiers",
                  component: CashiersIndex,
                  meta: {
                    pageTitle: "Manage Cashiers",
                    icon: CashierManageIcon,
                  },
                },
                {
                  path: "cashiers/:cashierId",
                  name: "manageCashiersEdit",
                  component: CashierEdit,
                  props: true,
                  meta: {
                    pageTitle: "Edit Cashier",
                  },
                },
                {
                  path: "cashiers/new",
                  name: "manageCashiersCreate",
                  component: CashierCreate,
                  meta: {
                    pageTitle: "Add Cashier",
                  },
                },
              ],
            },
            {
              path: ":pathMatch(.*)*",
              redirect: {
                name: "manageProducts",
              },
            },
          ],
        },
      ],
    },
    {
      path: "/login",
      name: "login",
      component: PortalLogIn,
      meta: {
        pageTitle: "Log In",
      },
      beforeEnter: () => {
        return isLoggedIn.value
          ? {
              name: "home",
            }
          : true
      },
    },
    {
      path: "/merchant-group/login",
      name: "loginMerchantGroup",
      component: MerchantGroupLogin,
      meta: {
        pageTitle: "Log In",
      },
      beforeEnter: () => {
        return isLoggedInMerchantGroup.value
          ? {
              name: "homeMerchantGroup",
            }
          : true
      },
    },
    {
      path: "/handle-login",
      name: "handleLoginLink",
      component: PortalHandleLoginLink,
    },
    {
      path: "/handle-merchant-group-login",
      name: "handleMerchantGroupLoginLink",
      component: PortalHandleMerchantGroupLoginLink,
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: {
        name: "home",
      },
    },
  ],
})

router.beforeEach((to) => {
  setPageTitle(to.meta.pageTitle || "")

  if (to.meta.auth && !isLoggedIn.value) {
    return {
      name: "login",
    }
  }

  if (
    to.meta.merchantGroupAuth &&
    !isLoggedInMerchantGroup.value
  ) {
    return {
      name: "loginMerchantGroup",
    }
  }

  return true
})

export default router
